/**
 * Read a Google Maps directions link: the stops, any dragged via points, the
 * travel mode, and the "Depart at" / "Arrive by" time the user set.
 *
 * Google documents only the `?api=1` form, and that form has no time. Links
 * copied from the address bar or a share sheet use the undocumented
 * `/maps/dir/<stop>/<stop>/@…/data=!…` form instead, where everything past the
 * stop names lives in a `!`-separated protobuf-ish blob. The fields read here
 * are the ones whose meaning is stable across years of links in the wild:
 *
 * - `!2m2!1d<lng>!2d<lat>` or, in current share links, `!8m2!3d<lat>!4d<lng>`:
 *   coordinates of a stop picked from search, in order
 * - `!3m4!1m2!1d<lng>!2d<lat>!3s…`: a via point made by dragging the route
 * - `!6e<k>…!8j<seconds>`: the time option, `k = 0` depart at, `k = 1` arrive by,
 *   and `seconds` is the chosen local wall-clock time encoded as if it were UTC
 * - `!3e<m>`: travel mode, 0 driving, 1 cycling, 2 walking, 3 transit
 * - `!1b1` / `!2b1` / `!3b1` inside the same options block as the time: avoid
 *   highways / tolls / ferries
 *
 * Nothing here touches the network. Short links (`maps.app.goo.gl/…`) must be
 * expanded first, which needs a server because the redirect carries no CORS
 * headers; see `functions/api/resolve.ts`.
 */

export type LatLng = { lat: number; lng: number };

export type Stop = {
  /** What the link calls the stop: a place name, an address, or "lat,lng". */
  label: string;
  /** Known when the link carries coordinates for this stop. */
  point?: LatLng;
  /** The stop is "Your location": the device's position when the link was made. */
  currentLocation?: boolean;
};

/** A clock reading with no time zone attached, as Google stores it. */
export type WallTime = { year: number; month: number; day: number; hour: number; minute: number };

export type TimeConstraint =
  | { kind: "now" }
  | { kind: "depart"; wall: WallTime }
  | { kind: "arrive"; wall: WallTime };

export type TravelMode = "driving" | "bicycling" | "walking" | "transit";

/** Route options set under "Options" in Google Maps. */
export type Avoid = { highways: boolean; tolls: boolean; ferries: boolean };

export type ParsedLink = {
  stops: Stop[];
  avoid: Avoid;
  /** Points the user dragged the route through, in the order they appear in the link. */
  via: LatLng[];
  time: TimeConstraint;
  travelMode: TravelMode | null;
};

export type ParseResult = { ok: true; link: ParsedLink } | { ok: false; error: string };

const NUMBER = String.raw`-?\d+(?:\.\d+)?`;
const STOP_POINT = new RegExp(String.raw`!2m2!1d(${NUMBER})!2d(${NUMBER})|!8m2!3d(${NUMBER})!4d(${NUMBER})`, "g");
const VIA_POINT = new RegExp(String.raw`!3m4!1m2!1d(${NUMBER})!2d(${NUMBER})!3s`, "g");
const TIME = /!6e(\d)(?:!7e\d+)?!8j(\d+)/;
const MODE = /!3e(\d)/g;
const LAT_LNG = new RegExp(String.raw`^\s*(${NUMBER})\s*,\s*(${NUMBER})\s*$`);

const MODES: Record<string, TravelMode> = {
  "0": "driving",
  "1": "bicycling",
  "2": "walking",
  "3": "transit",
};

/** The first http(s) URL in a pasted string; share sheets wrap the link in prose. */
export function extractUrl(text: string): string | null {
  const match = text.match(/https?:\/\/[^\s<>"']+/);
  return match ? match[0] : null;
}

/** True for links that must be expanded before they can be parsed. */
export function isShortLink(url: string): boolean {
  try {
    const { hostname, pathname } = new URL(url);
    if (hostname === "maps.app.goo.gl") return true;
    return hostname === "goo.gl" && pathname.startsWith("/maps");
  } catch {
    return false;
  }
}

function isGoogleMapsUrl(url: URL): boolean {
  const host = url.hostname.replace(/^www\./, "");
  const google = /^google\.[a-z.]+$/.test(host) || /^maps\.google\.[a-z.]+$/.test(host);
  return google && (url.pathname.startsWith("/maps") || host.startsWith("maps."));
}

/** "lat,lng" → point, if the label is literally coordinates. */
export function parseLatLng(text: string): LatLng | null {
  const match = text.match(LAT_LNG);
  if (!match) return null;
  const lat = Number(match[1]);
  const lng = Number(match[2]);
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

const decode = (segment: string): string => {
  try {
    return decodeURIComponent(segment.replace(/\+/g, " ")).trim();
  } catch {
    return segment.replace(/\+/g, " ").trim();
  }
};

function toStop(label: string): Stop {
  if (!label) return { label: "Your location", currentLocation: true };
  const point = parseLatLng(label);
  return point ? { label, point } : { label };
}

const pairs = (data: string, pattern: RegExp): LatLng[] =>
  [...data.matchAll(pattern)].map((match) => ({ lng: Number(match[1]), lat: Number(match[2]) }));

/** Stop coordinates in either encoding, in the order they appear. */
function stopPoints(data: string): LatLng[] {
  return [...data.matchAll(STOP_POINT)].map((match) =>
    match[1] !== undefined
      ? { lng: Number(match[1]), lat: Number(match[2]) }
      : { lat: Number(match[3]), lng: Number(match[4]) },
  );
}

/** Read the `8j` seconds as a wall clock: Google stores local time as if it were UTC. */
export function wallTimeFromSeconds(seconds: number): WallTime {
  const date = new Date(seconds * 1000);
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
    hour: date.getUTCHours(),
    minute: date.getUTCMinutes(),
  };
}

function readTime(data: string): TimeConstraint {
  const match = data.match(TIME);
  if (!match) return { kind: "now" };
  const wall = wallTimeFromSeconds(Number(match[2]));
  if (match[1] === "0") return { kind: "depart", wall };
  if (match[1] === "1") return { kind: "arrive", wall };
  // 2 is transit's "last available", which has no meaning for a drive.
  return { kind: "now" };
}

const OPTION_KEYS = new Set(["1b", "2b", "3b", "6e", "7e", "8j"]);

/**
 * The tokens of the route-options block: a `!2m<n>` whose next `n` tokens are
 * all option fields. Stop coordinates are also `!2m2` blocks, but of `1d`/`2d`,
 * so the key check tells the two apart.
 */
function optionTokens(data: string): Map<string, string> {
  const tokens = data.split("!").filter(Boolean);
  for (let index = 0; index < tokens.length; index++) {
    const head = tokens[index]?.match(/^2m(\d+)$/);
    if (!head) continue;
    const body = tokens.slice(index + 1, index + 1 + Number(head[1]));
    const fields = body.map((token) => [token.slice(0, token.search(/[a-z]/) + 1), token.slice(token.search(/[a-z]/) + 1)]);
    if (fields.length > 0 && fields.every(([key]) => OPTION_KEYS.has(key ?? ""))) {
      return new Map(fields as [string, string][]);
    }
  }
  return new Map();
}

export const NO_AVOID: Avoid = { highways: false, tolls: false, ferries: false };

function readAvoid(data: string): Avoid {
  const options = optionTokens(data);
  return { highways: options.get("1b") === "1", tolls: options.get("2b") === "1", ferries: options.get("3b") === "1" };
}

function readMode(data: string): TravelMode | null {
  const all = [...data.matchAll(MODE)];
  const last = all[all.length - 1];
  return last ? (MODES[last[1] ?? ""] ?? null) : null;
}

/**
 * Give stops the coordinates the data blob carries for them.
 *
 * The blob lists coordinates only for stops that were picked from search, in
 * stop order. When the counts line up the assignment is unambiguous; when they
 * do not, the stops keep their labels and are geocoded later, which is slower
 * but never attaches one stop's coordinates to another.
 */
function attachPoints(stops: Stop[], points: LatLng[]): Stop[] {
  if (points.length === 0) return stops;
  if (points.length === stops.length) {
    return stops.map((stop, index) => (stop.currentLocation ? stop : { ...stop, point: points[index] }));
  }
  const unresolved = stops.filter((stop) => !stop.point && !stop.currentLocation);
  if (points.length !== unresolved.length) return stops;
  let next = 0;
  return stops.map((stop) => (stop.point || stop.currentLocation ? stop : { ...stop, point: points[next++] }));
}

function parseDirPath(url: URL): ParseResult {
  const parts = url.pathname.split("/");
  const dir = parts.indexOf("dir");
  const labels: string[] = [];
  let data = "";
  for (let index = dir + 1; index < parts.length; index++) {
    const part = parts[index] ?? "";
    if (part.startsWith("@")) continue;
    if (part.startsWith("data=")) {
      data = decode(parts.slice(index).join("/").slice("data=".length));
      break;
    }
    // A trailing slash leaves one empty segment that is not a stop.
    if (part === "" && index === parts.length - 1) continue;
    labels.push(decode(part));
  }
  // `?data=` also turns up on links rewritten by some apps.
  data ||= url.searchParams.get("data") ?? "";

  const stops = attachPoints(labels.map(toStop), stopPoints(data));
  if (stops.length < 2) return { ok: false, error: "The link needs at least a start and a destination." };

  return {
    ok: true,
    link: {
      stops,
      avoid: readAvoid(data),
      via: pairs(data, VIA_POINT),
      time: readTime(data),
      travelMode: readMode(data),
    },
  };
}

function parseApiForm(url: URL): ParseResult {
  const params = url.searchParams;
  const origin = params.get("origin") ?? "";
  const destination = params.get("destination");
  if (!destination) return { ok: false, error: "The link has no destination." };

  const waypoints = (params.get("waypoints") ?? "")
    .split("|")
    .map((value) => value.trim())
    .filter(Boolean);
  const mode = params.get("travelmode");
  const travelMode = mode && Object.values(MODES).includes(mode as TravelMode) ? (mode as TravelMode) : null;

  return {
    ok: true,
    link: {
      stops: [origin, ...waypoints, destination].map((label) => toStop(label.trim())),
      avoid: {
        highways: /highways/.test(params.get("avoid") ?? ""),
        tolls: /tolls/.test(params.get("avoid") ?? ""),
        ferries: /ferries/.test(params.get("avoid") ?? ""),
      },
      via: [],
      time: { kind: "now" },
      travelMode,
    },
  };
}

/**
 * Coordinates from one entry of the legacy `geocode=` parameter: URL-safe
 * base64 of a small protobuf whose field 2 is latitude and field 3 longitude,
 * each a signed fixed32 in microdegrees. Other fields (place ids) are skipped.
 */
export function decodeGeocodeEntry(entry: string): LatLng | null {
  let bytes: Uint8Array;
  try {
    const binary = atob(entry.replace(/-/g, "+").replace(/_/g, "/"));
    bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  } catch {
    return null;
  }
  const view = new DataView(bytes.buffer);
  let lat: number | null = null;
  let lng: number | null = null;
  let offset = 0;
  const varint = () => {
    let value = 0;
    let shift = 0;
    while (offset < bytes.length) {
      const byte = bytes[offset++]!;
      value += (byte & 0x7f) * 2 ** shift;
      if (byte < 0x80) return value;
      shift += 7;
    }
    return value;
  };
  while (offset < bytes.length) {
    const key = varint();
    const field = Math.floor(key / 8);
    const wire = key % 8;
    if (wire === 5) {
      if (offset + 4 > bytes.length) break;
      const value = view.getInt32(offset, true) / 1e6;
      if (field === 2) lat = value;
      if (field === 3) lng = value;
      offset += 4;
    } else if (wire === 1) offset += 8;
    else if (wire === 0) varint();
    else if (wire === 2) offset += varint();
    else break;
  }
  if (lat === null || lng === null || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

/**
 * The old `?saddr=A&daddr=B+to:C` form, which iOS share sheets still produce.
 * `geocode=` carries one coordinate entry per stop; `dirflg` carries the mode
 * (d, w, r, b) and avoid letters (h highways, t tolls, f ferries).
 */
function parseLegacyForm(url: URL): ParseResult {
  const saddr = url.searchParams.get("saddr") ?? "";
  const daddr = url.searchParams.get("daddr");
  if (!daddr) return { ok: false, error: "The link has no destination." };
  const labels = [saddr, ...daddr.split(/\s+to:/)].map((label) => label.trim());
  let stops = labels.map(toStop);

  const geocode = (url.searchParams.get("geocode") ?? "").split(";");
  if (geocode.length === stops.length) {
    stops = stops.map((stop, index) => {
      if (stop.currentLocation) return stop;
      const point = decodeGeocodeEntry(geocode[index] ?? "");
      return point ? { ...stop, point } : stop;
    });
  }

  const flags = url.searchParams.get("dirflg") ?? "";
  const mode = flags.match(/[dwrb]/)?.[0];
  const travelMode: TravelMode | null =
    mode === "d" ? "driving" : mode === "w" ? "walking" : mode === "r" ? "transit" : mode === "b" ? "bicycling" : null;
  return {
    ok: true,
    link: {
      stops,
      avoid: { highways: flags.includes("h"), tolls: flags.includes("t"), ferries: flags.includes("f") },
      via: [],
      time: { kind: "now" },
      travelMode,
    },
  };
}

/** Parse a pasted Google Maps directions link (or text containing one). */
export function parseMapsLink(input: string): ParseResult {
  // Links copied out of HTML (some share sheets) arrive with `&amp;` for `&`.
  const raw = (extractUrl(input) ?? input.trim()).replace(/&amp;/g, "&");
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return { ok: false, error: "That doesn't look like a link." };
  }

  if (isShortLink(url.toString())) {
    return { ok: false, error: "Short links have to be expanded first." };
  }
  if (!isGoogleMapsUrl(url)) return { ok: false, error: "That isn't a Google Maps link." };

  if (url.pathname.includes("/dir/")) {
    if (url.searchParams.get("api") === "1" && /\/dir\/?$/.test(url.pathname)) return parseApiForm(url);
    return parseDirPath(url);
  }
  if (url.searchParams.get("api") === "1" && url.pathname.startsWith("/maps/dir")) return parseApiForm(url);
  if (url.searchParams.has("daddr")) return parseLegacyForm(url);
  if (url.pathname.includes("/place/") || url.pathname.includes("/search/")) {
    return { ok: false, error: "That's a link to a place. Open Directions in Google Maps and share that link." };
  }
  return { ok: false, error: "That Google Maps link has no directions in it." };
}

/** A wall time as `YYYY-MM-DDTHH:MM`, the value an `<input type="datetime-local">` takes. */
export function formatWallTime(wall: WallTime): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${wall.year}-${pad(wall.month)}-${pad(wall.day)}T${pad(wall.hour)}:${pad(wall.minute)}`;
}

/** Inverse of {@link formatWallTime}; `null` on anything malformed. */
export function parseWallTime(text: string): WallTime | null {
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) return null;
  const [year, month, day, hour, minute] = match.slice(1).map(Number) as [number, number, number, number, number];
  return { year, month, day, hour, minute };
}
